import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';

interface ConnectionHubProps {
    onBack: () => void;
}

const ConnectionHub: React.FC<ConnectionHubProps> = ({ onBack }) => {
    const [selectedService, setSelectedService] = useState<'contractors' | 'financing' | 'incentives'>('contractors');
    const location = useLocation();
    
    // Check URL parameters for tab selection
    useEffect(() => {
        const params = new URLSearchParams(location.search);
        const tab = params.get('tab');
        if (tab === 'contractors' || tab === 'financing' || tab === 'incentives') {
            setSelectedService(tab);
        }
    }, [location]);

    const contractors = [
        {
            name: "EcoRetrofit NYC",
            specialty: "LL97 Compliance & Energy Efficiency",
            rating: 4.8,
            projects: 120,
            contact: "info@ecoretrofit.com",
            referralFee: "3% of project value"
        },
        {
            name: "GreenBuild Solutions",
            specialty: "Commercial HVAC & Building Envelope",
            rating: 4.9,
            projects: 85,
            contact: "contact@greenbuildsolutions.com",
            referralFee: "2.5% of project value"
        },
        {
            name: "Urban Energy Partners",
            specialty: "Large Commercial Retrofits",
            rating: 4.7,
            projects: 95,
            contact: "hello@urbanenergypartners.com",
            referralFee: "3.5% of project value"
        }
    ];

    const lenders = [
        {
            name: "NYC CPACE Program",
            type: "CPACE Financing",
            rates: "3.5% - 5.5%",
            terms: "Up to 30 years",
            contact: "info@nyccpace.org",
            referralFee: "0.5% of loan amount"
        },
        {
            name: "GreenWorks Lending",
            type: "Energy Efficiency Loans",
            rates: "4.0% - 6.0%",
            terms: "5-20 years",
            contact: "lending@greenworks.com",
            referralFee: "1% of loan amount"
        },
        {
            name: "Sustainable Capital",
            type: "Commercial Energy Finance",
            rates: "3.8% - 7.2%",
            terms: "10-25 years",
            contact: "info@sustainablecapital.com",
            referralFee: "0.75% of loan amount"
        }
    ];

    const incentives = [
        {
            program: "NYSERDA FlexTech Program",
            description: "Technical assistance for energy efficiency studies",
            amount: "Up to $100,000",
            eligibility: "Commercial buildings 25,000+ sq ft",
            deadline: "Rolling applications"
        },
        {
            program: "Con Ed Energy Efficiency Programs",
            description: "Rebates for HVAC, lighting, and building envelope improvements",
            amount: "Up to $1M per customer",
            eligibility: "Con Ed commercial customers",
            deadline: "December 2025"
        },
        {
            program: "NYC Accelerated Conservation and Efficiency (ACE)",
            description: "Financing for energy efficiency improvements",
            amount: "Up to $20M per project",
            eligibility: "Buildings subject to LL97",
            deadline: "Ongoing"
        }
    ];

    return (
        <div className="min-h-screen bg-gray-50 py-8">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                {/* Header */}
                <div className="mb-8">
                    <button
                        onClick={onBack}
                        className="mb-4 text-blue-600 hover:text-blue-800 flex items-center"
                    >
                        ← Back to Dashboard
                    </button>
                    <h1 className="text-4xl font-bold text-gray-900 mb-2">Connection Hub</h1>
                    <p className="text-xl text-gray-600 mb-4">Connect property owners with contractors, lenders, and incentive programs</p>
                    
                    {/* Usage Instructions */}
                    <div className="mb-4 bg-blue-50 border border-blue-200 rounded-lg p-4">
                        <h3 className="text-lg font-semibold text-blue-900 mb-3 flex items-center">
                            <span className="mr-2">🤝</span> How to Use Connection Hub
                        </h3>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                            <div>
                                <h4 className="font-semibold text-blue-800 mb-2">For Property Owners:</h4>
                                <ul className="text-sm text-blue-700 space-y-1">
                                    <li>• Browse vetted contractors by specialty</li>
                                    <li>• Compare financing options & rates</li>
                                    <li>• Find incentive programs</li>
                                    <li>• Request quotes directly</li>
                                </ul>
                            </div>
                            <div>
                                <h4 className="font-semibold text-blue-800 mb-2">For Service Providers:</h4>
                                <ul className="text-sm text-blue-700 space-y-1">
                                    <li>• Join our verified network</li>
                                    <li>• Receive qualified leads</li>
                                    <li>• Showcase your portfolio</li>
                                    <li>• Build partnerships</li>
                                </ul>
                            </div>
                            <div>
                                <h4 className="font-semibold text-blue-800 mb-2">Revenue Features:</h4>
                                <ul className="text-sm text-blue-700 space-y-1">
                                    <li>• Track referral commissions</li>
                                    <li>• Monitor connection success</li>
                                    <li>• Automated earnings reports</li>
                                    <li>• Partnership management</li>
                                </ul>
                            </div>
                        </div>
                    </div>
                    
                    <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                        <p className="text-green-800 text-sm">
                            <strong>💰 Revenue Model:</strong> Generate referral commissions by connecting property owners with vetted service providers. Track all connections and calculate your earnings in real-time.
                        </p>
                    </div>
                </div>

                {/* Service Type Selector */}
                <div className="bg-white rounded-xl shadow-lg mb-8">
                    <div className="border-b border-gray-200">
                        <nav className="flex">
                            <button
                                onClick={() => setSelectedService('contractors')}
                                className={`px-6 py-4 text-sm font-medium ${
                                    selectedService === 'contractors'
                                        ? 'border-b-2 border-blue-500 text-blue-600'
                                        : 'text-gray-500 hover:text-gray-700'
                                }`}
                            >
                                🔧 Contractors
                            </button>
                            <button
                                onClick={() => setSelectedService('financing')}
                                className={`px-6 py-4 text-sm font-medium ${
                                    selectedService === 'financing'
                                        ? 'border-b-2 border-blue-500 text-blue-600'
                                        : 'text-gray-500 hover:text-gray-700'
                                }`}
                            >
                                💰 Financing
                            </button>
                            <button
                                onClick={() => setSelectedService('incentives')}
                                className={`px-6 py-4 text-sm font-medium ${
                                    selectedService === 'incentives'
                                        ? 'border-b-2 border-blue-500 text-blue-600'
                                        : 'text-gray-500 hover:text-gray-700'
                                }`}
                            >
                                🎁 Incentives
                            </button>
                        </nav>
                    </div>

                    <div className="p-8">
                        {selectedService === 'contractors' && (
                            <div>
                                <h2 className="text-2xl font-semibold text-gray-900 mb-6">Vetted LL97 Retrofit Contractors</h2>
                                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                                    {contractors.map((contractor, index) => (
                                        <div key={index} className="bg-gray-50 rounded-lg p-6 border border-gray-200">
                                            <h3 className="text-lg font-semibold text-gray-900 mb-2">{contractor.name}</h3>
                                            <p className="text-sm text-gray-600 mb-3">{contractor.specialty}</p>
                                            
                                            <div className="space-y-2 text-sm mb-4">
                                                <div className="flex justify-between">
                                                    <span className="text-gray-600">Rating:</span>
                                                    <span className="font-medium">⭐ {contractor.rating}/5</span>
                                                </div>
                                                <div className="flex justify-between">
                                                    <span className="text-gray-600">Projects:</span>
                                                    <span className="font-medium">{contractor.projects}</span>
                                                </div>
                                                <div className="flex justify-between">
                                                    <span className="text-gray-600">Contact:</span>
                                                    <span className="font-medium text-blue-600">{contractor.contact}</span>
                                                </div>
                                            </div>
                                            
                                            <div className="bg-green-100 p-3 rounded mb-4">
                                                <p className="text-xs text-green-800">
                                                    <strong>Your Commission:</strong> {contractor.referralFee}
                                                </p>
                                            </div>
                                            
                                            <a 
                                                href={`mailto:${contractor.contact}?subject=Property%20Owner%20Connection%20Request&body=I%20would%20like%20to%20connect%20a%20property%20owner%20with%20your%20services.`}
                                                className="w-full bg-blue-600 hover:bg-blue-700 text-white py-2 px-4 rounded-md text-sm transition-colors block text-center"
                                            >
                                                Connect Property Owner
                                            </a>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}

                        {selectedService === 'financing' && (
                            <div>
                                <h2 className="text-2xl font-semibold text-gray-900 mb-6">Energy Efficiency Financing Partners</h2>
                                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                                    {lenders.map((lender, index) => (
                                        <div key={index} className="bg-gray-50 rounded-lg p-6 border border-gray-200">
                                            <h3 className="text-lg font-semibold text-gray-900 mb-2">{lender.name}</h3>
                                            <p className="text-sm text-gray-600 mb-3">{lender.type}</p>
                                            
                                            <div className="space-y-2 text-sm mb-4">
                                                <div className="flex justify-between">
                                                    <span className="text-gray-600">Rates:</span>
                                                    <span className="font-medium">{lender.rates}</span>
                                                </div>
                                                <div className="flex justify-between">
                                                    <span className="text-gray-600">Terms:</span>
                                                    <span className="font-medium">{lender.terms}</span>
                                                </div>
                                                <div className="flex justify-between">
                                                    <span className="text-gray-600">Contact:</span>
                                                    <span className="font-medium text-blue-600">{lender.contact}</span>
                                                </div>
                                            </div>
                                            
                                            <div className="bg-green-100 p-3 rounded mb-4">
                                                <p className="text-xs text-green-800">
                                                    <strong>Your Commission:</strong> {lender.referralFee}
                                                </p>
                                            </div>
                                            
                                            <a 
                                                href={`mailto:${lender.contact}?subject=Financing%20Connection%20Request&body=I%20would%20like%20to%20connect%20for%20financing%20options.`}
                                                className="w-full bg-green-600 hover:bg-green-700 text-white py-2 px-4 rounded-md text-sm transition-colors block text-center"
                                            >
                                                Connect for Financing
                                            </a>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}

                        {selectedService === 'incentives' && (
                            <div>
                                <h2 className="text-2xl font-semibold text-gray-900 mb-6">Available Incentive Programs</h2>
                                <div className="space-y-6">
                                    {incentives.map((incentive, index) => (
                                        <div key={index} className="bg-gray-50 rounded-lg p-6 border border-gray-200">
                                            <div className="flex justify-between items-start mb-4">
                                                <h3 className="text-lg font-semibold text-gray-900">{incentive.program}</h3>
                                                <span className="bg-purple-100 text-purple-800 text-xs px-2 py-1 rounded-full">
                                                    {incentive.amount}
                                                </span>
                                            </div>
                                            
                                            <p className="text-gray-700 mb-4">{incentive.description}</p>
                                            
                                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                                                <div>
                                                    <span className="text-gray-600">Eligibility:</span>
                                                    <p className="font-medium">{incentive.eligibility}</p>
                                                </div>
                                                <div>
                                                    <span className="text-gray-600">Deadline:</span>
                                                    <p className="font-medium">{incentive.deadline}</p>
                                                </div>
                                            </div>
                                            
                                            <div className="mt-4 flex space-x-3">
                                                <a 
                                                    href={`mailto:incentives@nyc-retrofits.com?subject=Application%20Assistance%20for%20${encodeURIComponent(incentive.program)}&body=I%20would%20like%20assistance%20applying%20for%20the%20${encodeURIComponent(incentive.program)}%20incentive.`}
                                                    className="bg-purple-600 hover:bg-purple-700 text-white py-2 px-4 rounded-md text-sm transition-colors text-center"
                                                >
                                                    Application Assistance
                                                </a>
                                                <a 
                                                    href={`/program-details/${encodeURIComponent(incentive.program.toLowerCase().replace(/\s+/g, '-'))}`}
                                                    target="_blank"
                                                    rel="noopener noreferrer" 
                                                    className="bg-gray-600 hover:bg-gray-700 text-white py-2 px-4 rounded-md text-sm transition-colors text-center"
                                                    onClick={(e) => {
                                                        e.preventDefault();
                                                        alert(`Program details for ${incentive.program} would download here. This feature is coming soon.`);
                                                    }}
                                                >
                                                    Download Info
                                                </a>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ConnectionHub;
